import { useState, type CSSProperties, type HTMLAttributes, type ImgHTMLAttributes } from "react";
import "./skeleton.css";

export type SkeletonVariant = "line" | "control" | "avatar" | "image";
export type SkeletonGridVariant = "card" | "persona" | "model" | "row";

interface SkeletonProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  variant?: SkeletonVariant;
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  label?: string;
}

function statusProps(label?: string) {
  return label ? { role: "status" as const, "aria-label": label } : { "aria-hidden": true };
}

export function Skeleton({ variant = "line", width, height, label, className = "", style, ...props }: SkeletonProps) {
  return <span
    className={`skeleton skeleton-${variant}${className ? ` ${className}` : ""}`}
    style={{ ...style, width, height }}
    {...statusProps(label)}
    {...props}
  >{label ? <span className="sr-only">{label}</span> : null}</span>;
}

export function SkeletonLines({ lines = 3, label, className = "" }: { lines?: number; label?: string; className?: string }) {
  return <div className={`skeleton-lines${className ? ` ${className}` : ""}`} {...statusProps(label)}>
    {label ? <span className="sr-only">{label}</span> : null}
    {Array.from({ length: lines }, (_, index) => <Skeleton key={index} />)}
  </div>;
}

export function SkeletonGrid({ items = 6, variant = "card", label, className = "" }: { items?: number; variant?: SkeletonGridVariant; label?: string; className?: string }) {
  return <div className={`skeleton-grid skeleton-grid-${variant}${className ? ` ${className}` : ""}`} {...statusProps(label)}>
    {label ? <span className="sr-only">{label}</span> : null}
    {Array.from({ length: items }, (_, index) => <span className={`skeleton-card skeleton-card-${variant}`} aria-hidden="true" key={index}>
      {variant !== "row" ? <span className="skeleton skeleton-card-media" /> : <span className="skeleton skeleton-avatar" />}
      <span className="skeleton-card-copy"><span className="skeleton skeleton-line" /><span className="skeleton skeleton-line skeleton-line-short" /></span>
    </span>)}
  </div>;
}

export function SkeletonImage({ frameClassName = "", fallbackLabel = "图片暂不可用", className = "", onLoad, onError, ...props }: ImgHTMLAttributes<HTMLImageElement> & { frameClassName?: string; fallbackLabel?: string }) {
  const [state, setState] = useState<"loading" | "loaded" | "error">("loading");
  return <span className={`skeleton-image-frame is-${state}${frameClassName ? ` ${frameClassName}` : ""}`}>
    {state === "loading" ? <Skeleton variant="image" /> : null}
    {state === "error" ? <span className="skeleton-image-error" role="img" aria-label={fallbackLabel}>!</span> : null}
    <img
      {...props}
      className={className}
      onLoad={(event) => { setState("loaded"); onLoad?.(event); }}
      onError={(event) => { setState("error"); onError?.(event); }}
    />
  </span>;
}
